/**
 * Axios API Client Configuration
 *
 * This file creates and configures an Axios instance for making API requests.
 * It sets up:
 * - Base URL from environment variables
 * - Request interceptor to attach JWT token
 * - Response interceptor for error handling
 *
 * All API modules (authApi, extractionApi, etc.) should import this configured instance.
 */

import axios from 'axios';

/**
 * Create Axios instance with default configuration
 *
 * The base URL comes from environment variables set in .env file.
 * During development: VITE_API_URL=http://localhost:3000/api
 */
const api = axios.create({
  // Base URL for all requests (e.g., http://localhost:3000/api)
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3000/api',

  // Default headers for all requests
  headers: {
    'Content-Type': 'application/json'
  },

  // Request timeout (120 seconds - needed for AI extraction with sections)
  timeout: 120000
});

/**
 * Request Interceptor
 *
 * This runs before every request is sent.
 * It attaches the JWT token from localStorage to the Authorization header.
 */
api.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem('token');

    // If token exists, add it to the Authorization header
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    // Handle request error
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 *
 * This runs after every response is received.
 * It handles common error cases like:
 * - 401 Unauthorized: Token expired or invalid
 * - Network errors
 */
api.interceptors.response.use(
  // Successful response - just return the data
  (response) => response.data,

  // Error response - handle common cases
  (error) => {
    // Handle 401 Unauthorized (token expired)
    if (error.response?.status === 401) {
      // Clear invalid token
      localStorage.removeItem('token');

      // Redirect to login page (if not already there)
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    // Extract error message from response
    const message = error.response?.data?.error
      || error.message
      || 'An unexpected error occurred';

    // Create a more informative error object
    const enhancedError = new Error(message);
    enhancedError.status = error.response?.status;
    enhancedError.data = error.response?.data;

    return Promise.reject(enhancedError);
  }
);

export default api;
